import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, ChefHat, Loader2 } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { API_BASE } from '../services/api/_client'

/**
 * #1217 アプリ不要の受け取り状況ページ (`/o/:orderId?t=…`)。
 *
 * 店頭でレジ注文した客に渡すレシートの QR から開く。アプリも会員登録も不要で、
 * 「自分の番号が呼ばれたか」だけが分かる。番号掲示を見に行かなくても席から
 * 確認できるのが狙い (マックの受け取り体験をアプリなしで)。
 *
 * 認可は HMAC トークンのみ。返すのは番号・状態・店名・品数だけで、個人情報も
 * 操作 (キャンセル等) も載せない — 所持ベースの弱い認可に見合う情報量に絞る。
 *
 * ログイン前提の画面ではないので、apiFetch (認証トークン付与) ではなく素の
 * fetch を使う。
 */

const POLL_MS = 10_000

type Status = {
  callNumber: string | null
  status: string
  storeName: string | null
  itemCount: number
  // #1217 厨房 FIFO で自分より前に待っている組数 (received/preparing の間のみ意味を持つ)
  aheadCount?: number
}

export default function PublicOrderStatus() {
  const { orderId } = useParams()
  const [searchParams] = useSearchParams()
  const { t } = useI18n()
  const [data, setData] = useState<Status | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!orderId) return
    const token = searchParams.get('t') || ''
    let cancelled = false

    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/orders/${orderId}/public-status?t=${encodeURIComponent(token)}`,
        )
        if (!res.ok) throw new Error('not found')
        const body = (await res.json()) as Status
        if (!cancelled) { setData(body); setError(false) }
      } catch {
        if (!cancelled && !data) setError(true)
      }
    }

    void load()
    // 呼ばれたかを知るための画面なので定期更新する (受け渡し済みまで)
    const timer = window.setInterval(() => void load(), POLL_MS)
    return () => { cancelled = true; window.clearInterval(timer) }
    // token / orderId 以外では張り直さない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white px-6">
        <p className="text-center font-body text-sm text-text-secondary">
          {t('publicStatus.notFound')}
        </p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        <Loader2 size={28} className="animate-spin text-navy" />
      </div>
    )
  }

  const isReady = data.status === 'ready'
  const isPickedUp = data.status === 'picked_up'
  const isPreparing = data.status === 'preparing'
  const isReceived = data.status === 'received'
  // 待ち組数は受付/調理中の間だけ意味を持つ (呼び出し後は不要)
  const showQueue = (isReceived || isPreparing) && typeof data.aheadCount === 'number'

  const headline = isPickedUp
    ? t('publicStatus.pickedUp')
    : isReady
      ? t('publicStatus.ready')
      : isPreparing
        ? t('publicStatus.preparing')
        : t('publicStatus.received')
  const hint = isPickedUp
    ? t('publicStatus.pickedUpHint')
    : isReady
      ? t('publicStatus.readyHint')
      : isPreparing
        ? t('publicStatus.preparingHint')
        : t('publicStatus.receivedHint')

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-warm-white px-6 safe-top safe-bottom">
      <p className="font-body text-sm text-text-secondary">{data.storeName}</p>

      <div
        className={`mt-4 w-full max-w-sm rounded-3xl border-2 p-8 text-center ${
          isReady ? 'border-matcha bg-matcha/5' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="flex justify-center">
          {isReady || isPickedUp ? (
            <CheckCircle2 size={28} className="text-matcha" />
          ) : (
            <ChefHat size={28} className={isPreparing ? 'text-gold' : 'text-text-tertiary'} />
          )}
        </div>
        <p className="mt-2 font-body text-sm font-medium text-text-secondary">
          {headline}
        </p>
        <p
          className={`mt-2 font-display text-6xl font-bold ${
            isReady ? 'text-matcha' : 'text-text-primary'
          }`}
        >
          {data.callNumber || '—'}
        </p>

        {/* #1217 待ち組数 — 厨房 FIFO で自分より前に待っている組数。0 なら次に呼ぶ。 */}
        {showQueue && (
          <p className="mt-3 font-body text-sm font-medium text-gold">
            {(data.aheadCount ?? 0) > 0
              ? t('publicStatus.aheadCount', { count: String(data.aheadCount) })
              : t('publicStatus.aheadNext')}
          </p>
        )}

        <p className="mt-3 font-body text-[11px] leading-relaxed text-text-tertiary">
          {hint}
        </p>
      </div>

      <p className="mt-6 font-body text-[11px] text-text-tertiary">
        {t('publicStatus.autoRefresh')}
      </p>
    </div>
  )
}
